import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import ApiKeyDao from '@/interfaces/api-keys-management/ApiKey.dao'
import ApiKeyCreatedDao from '@/interfaces/api-keys-management/ApiKeyCreated.dao'
import CreateApiKeyDto from '@/interfaces/api-keys-management/CreateApiKey.dto'
import ApiKeyUsageLogDao, { ApiKeyUsageLogResponse } from '@/interfaces/api-keys-management/ApiKeyUsageLog.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'

const buildPagination = (total: number, page: number, limit: number): PageNumberPagination => {
    const safeLimit = limit > 0 ? limit : 1
    const pageCount = Math.max(1, Math.ceil(total / safeLimit))
    return {
        isFirstPage: page <= 1,
        isLastPage: page >= pageCount,
        currentPage: page,
        previousPage: page > 1 ? page - 1 : null,
        nextPage: page < pageCount ? page + 1 : null,
        pageCount,
        totalCount: total,
    }
}

export default class ApiKeysManagementService extends ApiService {
    async getApiKeys(params?: IRequestParameters): Promise<[ApiKeyDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ApiKeyDao[], PageNumberPagination]>(
            'GET',
            '/api-keys',
            params,
            undefined,
            undefined,
            'getApiKeys',
        ).catch(() => null)
    }

    async createApiKey(payload: CreateApiKeyDto): Promise<ApiKeyCreatedDao | null> {
        return this.request<CreateApiKeyDto, ApiKeyCreatedDao>(
            'POST',
            '/api-keys',
            undefined,
            payload,
            undefined,
            'createApiKey',
        ).catch(() => null)
    }

    async revokeApiKey(id: number): Promise<unknown | null> {
        return this.request<undefined, unknown>(
            'DELETE',
            `/api-keys/${id}`,
            undefined,
            undefined,
            undefined,
            'revokeApiKey',
        ).catch(() => null)
    }

    async rotateApiKey(id: number): Promise<ApiKeyCreatedDao | null> {
        return this.request<undefined, ApiKeyCreatedDao>(
            'POST',
            `/api-keys/${id}/rotate`,
            undefined,
            undefined,
            undefined,
            'rotateApiKey',
        ).catch(() => null)
    }

    async getApiKeyUsageLogs(params?: IRequestParameters): Promise<[ApiKeyUsageLogDao[], PageNumberPagination] | null> {
        const { apiKeyId, ...rest } = (params || {}) as Record<string, unknown>
        const id = Number(apiKeyId)
        if (!id) return null

        delete (rest as Record<string, unknown>).filter
        delete (rest as Record<string, unknown>).sortBy

        const limit = Number(rest.limit) || 50
        const page = Number(rest.page) || 1
        const queryParams: Record<string, unknown> = { ...rest, limit, page }
        for (const [key, value] of Object.entries(queryParams)) {
            if (value instanceof Date) queryParams[key] = value.toISOString()
        }

        const response = await this.request<undefined, ApiKeyUsageLogResponse>(
            'GET',
            `/api-keys/${id}/usage`,
            queryParams,
            undefined,
            undefined,
            'getApiKeyUsageLogs',
        ).catch(() => null)
        if (!response) return null
        return [
            response.data || [],
            buildPagination(response.total || 0, response.page || page, response.limit || limit),
        ]
    }
}
